import { z } from "zod";

import { reportSchemaError } from "@/diagnostics/schema-error.ts";

/**
 * Deep links: the selected listing lives in the query string.
 *
 * Two things fall out of that. The address bar is always a link worth sending
 * someone, and a link someone sends you opens on the unit they meant rather
 * than on the default view of the map.
 *
 * The listing id is the whole payload. Filters deliberately stay out of the URL
 * — a shared link is about one unit, and opening it should not overwrite the
 * recipient's own search, which is the thing they'd have to rebuild by hand.
 */

const PARAM = "listing";

/**
 * Ids are `"<source>:<sourceId>"` — see `ListingSchema.id`. A URL is
 * hand-editable, so the shape is checked rather than trusted, and the length
 * bound keeps a pathological query string out of the render path.
 */
export type SharedListingId = z.infer<typeof SharedListingIdSchema>;
export const SharedListingIdSchema = z
  .string()
  .min(3)
  .max(256)
  .regex(/^[\w.-]+:.+$/, 'expected "<source>:<sourceId>"');

/** The raw param, unvalidated — only for comparing against what we're about to write. */
function currentParam(): string | null {
  return new URLSearchParams(window.location.search).get(PARAM);
}

function pathWith(id: string | null): string {
  const url = new URL(window.location.href);
  if (id === null) url.searchParams.delete(PARAM);
  else url.searchParams.set(PARAM, id);
  return `${url.pathname}${url.search}${url.hash}`;
}

/** The listing id in the current URL, or null when there isn't a usable one. */
export function readSharedListingId(): string | null {
  const raw = currentParam();
  if (raw === null) return null;

  const parsed = SharedListingIdSchema.safeParse(raw);
  if (!parsed.success) {
    // Non-silent, per the house rule. The alternative is a map that
    // inexplicably ignores its own URL, which reads as the app being broken
    // rather than as the link being malformed.
    reportSchemaError("SharedListingId", parsed.error);
    return null;
  }
  return parsed.data;
}

/** Absolute link to one listing — what the share button hands out. */
export function shareUrlFor(id: string): string {
  const url = new URL(window.location.href);
  url.searchParams.set(PARAM, id);
  url.hash = "";
  return url.toString();
}

/**
 * Point the address bar at `id`, or at no listing.
 *
 * Opening a listing *pushes*, so Back returns to whatever you were looking at
 * before it. Closing one *replaces*, which is the important half: closing is
 * not a navigation. Popping the entry instead made closing a card equivalent to
 * pressing Back, so shutting the second listing you opened silently reopened
 * the first — the card came back on its own after you dismissed it.
 *
 * The early return is load-bearing beyond being an optimisation: it's what
 * stops this and the popstate subscription below from driving each other in a
 * loop, since restoring a selection from a URL would otherwise write that same
 * URL straight back.
 */
export function syncSharedListing(id: string | null): void {
  if (currentParam() === id) return;

  if (id !== null) history.pushState({}, "", pathWith(id));
  else history.replaceState(history.state, "", pathWith(null));
}

/** Subscribe to Back/Forward. Returns the unsubscribe. */
export function onSharedListingChange(fn: (id: string | null) => void): () => void {
  const handler = () => fn(readSharedListingId());
  window.addEventListener("popstate", handler);
  return () => window.removeEventListener("popstate", handler);
}
